import type { FC } from 'react'
import { useMemo } from 'react'
import styled from '@emotion/styled'

import type { Document } from '@contentful/rich-text-types'
import type { Options as RichTextRenderOptions } from '@contentful/rich-text-react-renderer'

import { BLOCKS } from '@contentful/rich-text-types'

import Typography from '@segment/matcha/components/Typography'
import Divider from '@segment/matcha/components/Divider'
import ButtonText from '@segment/matcha/components/ButtonText'
import Image from '@segment/matcha/components/Image'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import RichText from '@segment/matcha/recipes/RichText'

import type { CustomerStory } from '@segment/api/routes/customers/queries'

import contentfulImageLoader from '@segment/utils/contentfulImageLoader/contentfulImageLoader'

const copy = {
  heading: `Results`,
  linkText: `Request demo`,
}

const options: RichTextRenderOptions = {
  renderNode: {
    [BLOCKS.HEADING_4]: (_, children) => (
      <>
        <StyledDivider />
        <StyledRichText tag="h2" variant="h4">
          {children}
        </StyledRichText>
      </>
    ),
    [BLOCKS.PARAGRAPH]: (_, children) => <StyledRichText variant="pMedium">{children}</StyledRichText>,
  },
}

interface ResultsProps {
  data?: Document
  demoUrl: string
  logo: NonNullable<CustomerStory[`logo`]>
}
const Results: FC<ResultsProps> = ({ data, demoUrl, logo }) => {
  const content = useMemo(() => {
    if (!data) {
      return
    }

    return <RichText data={data} options={options} />
  }, [data])

  const { file, description } = logo.fields

  return (
    <Wrapper>
      <Logo
        loader={() =>
          contentfulImageLoader({
            src: file.url,
            width: Number(file.details.image?.width),
          })
        }
        width={file.details.image?.width}
        height={file.details.image?.height}
        src={file.url}
        alt={description}
      />

      {data && (
        <>
          <Heading variant="overline">{copy.heading}</Heading> <ContentWrapper>{content}</ContentWrapper>
        </>
      )}

      <ButtonText tag="a" size="medium" href={demoUrl} iconEnd={ChevronRightIcon}>
        {copy.linkText}
      </ButtonText>
    </Wrapper>
  )
}

export default Results

const Wrapper = styled.div`
  max-width: 320px;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    position: sticky;
    top: 160px;
    left: 0;
  }
`

const StyledRichText = styled(Typography)`
  margin: 0;
`

const Heading = styled(Typography)`
  color: ${({ theme }) => theme.palette.twilio.gray[80]};
`

const StyledDivider = styled(Divider)`
  margin: 16px 0;
`

const ContentWrapper = styled.div`
  margin-bottom: 32px;
`

const Logo = styled(Image)`
  max-height: 28px;
  object-position: left bottom;
  object-fit: contain;
  margin-bottom: 36px;
`
